/**
 * Tiered fetch wrapper for Supabase calls.
 *
 * Why tiered (not one flat timeout):
 *  • Supabase's server-side `statement_timeout` is set per role (prod):
 *    `anon = 3s`, `authenticated = 5s`, `authenticator = 8s`
 *    (tightened in migration `20260602110000`). A client timeout that sits
 *    too close to the server cap aborts milliseconds before the server would
 *    have returned a proper `57014` timeout error — losing the diagnostic
 *    signal and producing flaky behaviour under TLS / cold-start jitter.
 *    Note the cache client (cookie-less, anon key) runs as `anon`.
 *  • Different Supabase paths have different latency budgets:
 *      - middleware auth check        → must fail fast, fall through to anon
 *      - /auth/v1/*                   → user is explicitly waiting
 *      - GET  /rest/v1/*              → idempotent reads, NN/G 10s attention
 *      - POST /rest/v1/* and /rpc/*   → mutations, want server to finish
 *      - /storage/v1/*                → uploads, size-bounded
 *
 * Each layer's timeout is **strictly nested** inside the layer above (Vercel
 * Pro function default = 15s, so all per-call timeouts ≤ 15s for user-facing
 * paths). Service-role / admin clients get a longer cap (30s) because they
 * run from cron / settlement jobs that aren't user-blocking.
 */

export interface TieredTimeouts {
  /** GET /rest/v1/* — idempotent reads (default 10s). */
  read: number;
  /** POST/PATCH/DELETE /rest/v1/* and /rpc/* — mutations (default 15s). */
  mutation: number;
  /** /auth/v1/* — sign-in, getUser, refresh (default 12s). */
  auth: number;
  /** /storage/v1/* — uploads / downloads (default 60s). */
  storage: number;
  /** Fallback when path doesn't match any tier. */
  fallback: number;
  /**
   * 재시도 1회까지 포함한 **호출 전체**의 상한 (선택).
   *
   * 없으면 재시도도 첫 시도와 같은 예산을 그대로 받는다(기존 동작). 값이 있으면
   * 재시도 예산이 `totalBudget - (1차 소요 + 백오프)` 로 깎이고, 남은 시간이
   * {@link MIN_RETRY_TIMEOUT_MS} 보다 적으면 재시도 자체를 포기한다.
   * 티어 예산을 키우면서도 Vercel 함수 상한(15s) 안에 머물기 위한 장치다.
   */
  totalBudget?: number;
}

/** Standard tier for the per-user (cookie-bound) Supabase client. */
export const USER_CLIENT_TIMEOUTS: TieredTimeouts = {
  read: 10_000,
  mutation: 15_000,
  auth: 12_000,
  storage: 60_000,
  fallback: 15_000,
};

/**
 * Cache-client tier — cookie-less `anon` client used by `unstable_cache` /
 * SSR data fetches (home page, search, location lists). **서버에서만** 생성된다
 * (`lib/supabase/server.ts` 의 `createCacheClient`) — 브라우저는 `client.ts` 의
 * {@link USER_CLIENT_TIMEOUTS} 를 쓰므로 이 값은 사용자 기기 쪽 예산에 영향이 없다.
 *
 * 왜 3.5s → 8s 로 올렸나: 이 예산은 "쿼리 시간"이 아니라 **왕복 전체**를 잰다.
 * Postgres 쪽 실행은 수십 ms 인데(예: `get_product_branch_summaries` 24ms),
 * Supabase 리전에서 먼 사용자는 TLS 핸드셰이크 + MB 단위 응답 전송만으로
 * 3.5s 를 넘긴다. 그러면 검색이 조용히 0건이 됐다(LOCARORA-WEB-4S 계열).
 * 목록 payload 를 절반 이하로 줄인(→ `products/branch-summaries.ts`) 뒤에도
 * 먼 지역의 꼬리 지연을 흡수하려면 예산 자체가 필요하다.
 *
 * `totalBudget` 로 재시도까지 포함한 상한을 14s 로 묶어 Vercel 함수 상한(15s)
 * 안에 남는다: 1차 8s + 백오프 0.3s + 재시도 ≤ 5.7s.
 *
 * `retryReadOnlyRpc`: 이 클라이언트가 호출하는 `/rpc/*` 는 전부 읽기 전용
 * (`get_*` / `search_*`)이라 재생해도 안전하다 — 기본값(POST 는 재시도 금지)에서
 * 이 클라이언트만 예외로 빠져나온다. 자세한 근거는 `makeTimeoutFetch` 참고.
 */
export const CACHE_CLIENT_TIMEOUTS: TieredTimeouts = {
  read: 8_000,
  mutation: 8_000, // read-only RPCs (get_home_page_product_ids, etc.)
  auth: 12_000, // unused by cache client
  storage: 60_000, // unused by cache client
  fallback: 8_000,
  totalBudget: 14_000,
};

/**
 * Middleware tier — every request runs through here, so we cap aggressively
 * and let downstream gates treat anonymous as "not signed in" on timeout.
 * 4s is comfortably under Vercel's 25s middleware response window and the
 * Supabase 8s authenticated statement timeout.
 */
export const MIDDLEWARE_TIMEOUTS: TieredTimeouts = {
  read: 4_000,
  mutation: 4_000,
  auth: 4_000,
  storage: 4_000,
  fallback: 4_000,
};

/**
 * Service-role tier — admin/cron paths that legitimately take longer than
 * a user can wait (PDF generation, copy-product-image fanout, settlement
 * uploads). UX-blocking concerns don't apply here.
 */
export const ADMIN_CLIENT_TIMEOUTS: TieredTimeouts = {
  read: 30_000,
  mutation: 30_000,
  auth: 30_000,
  storage: 120_000,
  fallback: 30_000,
};

/** `totalBudget` 은 경로 티어가 아니라 호출 전체의 상한이라 제외한다. */
type Tier = Exclude<keyof TieredTimeouts, "totalBudget">;

function pickTier(url: string, method: string | undefined): Tier {
  if (url.includes("/storage/v1/")) return "storage";
  if (url.includes("/auth/v1/")) return "auth";
  if (url.includes("/rest/v1/")) {
    const isWrite = method && method !== "GET" && method !== "HEAD";
    return isWrite ? "mutation" : "read";
  }
  // /rpc/* — treat as mutation (could be either, mutation is the safer cap).
  if (url.includes("/rpc/")) return "mutation";
  return "fallback";
}

/**
 * 타임아웃/저속 호출의 소요 시간 보고. LOCARORA-WEB-4S 진단용 — Postgres 쪽은
 * 61일·143,636회 동안 최대 2.17초였는데 클라이언트는 3.5초에 끊긴다. 즉 예산의
 * 상당 부분이 쿼리 실행이 **아닌** 구간(PostgREST·pooler·Vercel↔Supabase 네트워크)
 * 에서 소모된다는 뜻이고, 어디인지는 실측 없이는 알 수 없다.
 *
 * 이 모듈은 브라우저(client.ts)·엣지 미들웨어에서도 로드되므로 Sentry를 직접
 * import하지 않는다. 서버 전용 진입점(server.ts)이 리포터를 주입한다.
 */
export interface SupabaseFetchTiming {
  /** 경로만 (쿼리스트링 제외 — PII·카디널리티 방지) */
  endpoint: string;
  method: string;
  tier: Tier;
  timeoutMs: number;
  elapsedMs: number;
  /** 1-based. 이 래퍼 내부 재시도 기준 (호출자 재시도는 별도) */
  attempt: number;
  /** timeout = 예산 초과로 중단, slow = 성공했지만 예산의 대부분을 소모 */
  outcome: "timeout" | "slow";
}

type TimingReporter = (timing: SupabaseFetchTiming) => void;

let timingReporter: TimingReporter | null = null;

/** server.ts가 Sentry 리포터를 꽂는다. null로 해제 가능(테스트). */
export function setSupabaseFetchTimingReporter(fn: TimingReporter | null): void {
  timingReporter = fn;
}

/** 성공했지만 예산의 이 비율을 넘게 쓴 호출은 "곧 터질 신호"로 함께 기록한다. */
const SLOW_SUCCESS_RATIO = 0.6;

function pathOf(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url.split("?")[0] ?? url;
  }
}

/**
 * ---------------------------------------------------------------------------
 * PostgREST 범위 초과(416 / PGRST103) → 빈 페이지
 * ---------------------------------------------------------------------------
 * PostgREST 는 필터된 행 수보다 큰 `offset` 요청에 `416` + 본문
 * `{"code":"PGRST103", ...}` + 슬래시 뒤에 **실제 총 행 수**가 담긴
 * `Content-Range` 로 답한다. supabase-js 는 이걸 쿼리 `error` 로 올리고 호출부
 * 대부분이 그대로 throw 하므로, 뒤로가기·북마크로 남은 `?page=N` 이나 "행이
 * 필터 밖으로 빠진 뒤의 재조회" 한 번이 목록 화면 전체를 에러 페이지로 바꾼다
 * (실제로 admin 파트너 주문에서 prod 사고로 터졌다 — LOCARORA-ADMIN-68).
 *
 * 페이지 번호에서 `.range()` 를 만드는 호출부를 하나씩 고치는 대신, 모든
 * Supabase 클라이언트(user/cache/middleware/browser)가 공유하는 이 전송 계층
 * 한 곳에서 중화한다.
 *
 * 설계 근거:
 *  • **200 + `[]`** — 낡은 페이지 번호가 화면을 깨뜨리면 안 된다. 빈 목록은
 *    사용자가 복구할 수 있는 상태지만, 에러 화면은 그렇지 않다.
 *  • **`Content-Range` 는 원본 그대로** — supabase-js 가 여기서 `count` 를 읽는다.
 *    슬래시 뒤 숫자가 실제 총 건수라, 합성 응답을 받아도 UI 는 올바른 총계·총
 *    페이지 수를 그리고 사용자는 유효한 페이지로 되돌아갈 수 있다.
 *  • **`/rest/v1/` 밖은 제외** — Storage 의 byte-range 416 은 진짜 오류다.
 *  • **`Accept: application/vnd.pgrst.object+json` 은 제외** — `.single()` 처럼
 *    단일 객체를 기대하는 파서에 `[]` 를 주면 그게 더 큰 고장이다. 이때는 416 을
 *    그대로 올려 보낸다.
 *  • **조용히 삼키지 않는다** — 정말 잘못 계산된 offset 은 아래 `console.warn`
 *    (요청 offset / limit / 실제 총 건수)으로 남는다. 이 모듈은 브라우저·엣지에서도
 *    로드되므로 Sentry 를 직접 import 하지 않는다(위 {@link SupabaseFetchTiming}
 *    주석과 같은 이유). 타이밍 리포터는 timeout/slow 전용이라 재사용하지 않는다.
 *
 * 본문의 `PGRST103` 까지 확인하므로 본문이 없는 `HEAD` 416(count 전용 조회)은
 * 합성되지 않고 그대로 통과한다. 합성이 일어나는 경우 `HEAD` 응답 본문은 비운다.
 */

/** PostgREST "Requested range not satisfiable" 의 에러 코드. */
const PGRST_RANGE_NOT_SATISFIABLE = "PGRST103";

/** `.single()` 이 붙이는 Accept — 이 요청에 `[]` 를 주면 파싱이 깨진다. */
const PGRST_SINGLE_OBJECT_ACCEPT = "application/vnd.pgrst.object+json";

function searchParamOf(url: string, key: string): string | null {
  try {
    return new URL(url).searchParams.get(key);
  } catch {
    const q = url.indexOf("?");
    if (q < 0) return null;
    return new URLSearchParams(url.slice(q + 1)).get(key);
  }
}

/** `Content-Range` 의 슬래시 뒤 총계 (알 수 없으면 null). */
function totalOfContentRange(value: string | null): string | null {
  const total = value?.split("/")[1];
  return total && total !== "*" ? total : null;
}

function acceptHeaderOf(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
): string {
  try {
    if (init?.headers) {
      const v = new Headers(init.headers).get("accept");
      if (v !== null) return v;
    }
    if (typeof input === "object" && "headers" in input) {
      return input.headers.get("accept") ?? "";
    }
  } catch {
    // 헤더를 못 읽으면 "단일 객체 요청"으로 단정하지 않고 빈 문자열로 둔다.
  }
  return "";
}

/**
 * 범위를 벗어난 PostgREST 읽기(416/PGRST103)를 빈 성공 페이지로 바꾼다.
 * 조건이 하나라도 어긋나면 원본 응답을 그대로 돌려준다.
 */
async function emptyPageOnOutOfRange(
  res: Response,
  url: string,
  method: string | undefined,
  accept: string,
): Promise<Response> {
  if (res.status !== 416) return res;
  if (!url.includes("/rest/v1/")) return res;
  if (accept.includes(PGRST_SINGLE_OBJECT_ACCEPT)) return res;

  const offset = searchParamOf(url, "offset");
  if (offset === null) return res;

  let code: unknown;
  try {
    // clone() 이라 어느 검사에서 되돌아가도 원본 본문은 그대로 쓸 수 있다.
    code = ((await res.clone().json()) as { code?: unknown } | null)?.code;
  } catch {
    return res; // PostgREST JSON 이 아니면 건드리지 않는다.
  }
  if (code !== PGRST_RANGE_NOT_SATISFIABLE) return res;

  const contentRange = res.headers.get("content-range");
  console.warn("[supabase-fetch] offset out of range -> empty page", {
    endpoint: pathOf(url),
    offset,
    limit: searchParamOf(url, "limit"),
    total: totalOfContentRange(contentRange),
  });

  const headers = new Headers({ "content-type": "application/json" });
  // supabase-js 는 Content-Range 에서 count 를 읽는다 → 실제 총 건수를 보존한다.
  if (contentRange) headers.set("content-range", contentRange);
  return new Response(method === "HEAD" ? null : "[]", {
    status: 200,
    headers,
  });
}

/**
 * A fetch rejection worth one retry: our own timeout abort (connection couldn't
 * be acquired / network stalled — e.g. a transient PostgREST pool spike or the
 * Vercel↔Supabase `write ETIMEDOUT` bursts behind LOCARORA-WEB-4S) or a
 * low-level connection error. NOT a caller abort and NOT a slow query — a
 * Postgres statement_timeout comes back as a normal result error (57014) below
 * the read cap, so it never reaches here.
 *
 * `AbortSignal.timeout` 은 `TimeoutError` 로 중단한다. 다만 런타임에 따라
 * (구형 undici / polyfill) 같은 상황을 `AbortError` 로 흘리는 경우가 있어
 * **호출자 signal 이 중단되지 않은 경우에 한해** AbortError 도 재시도 대상으로
 * 본다 — 호출자 취소는 `makeTimeoutFetch` 쪽에서 `init.signal.aborted` 로 먼저
 * 걸러내므로, 여기 도달한 AbortError 는 우리 타임아웃이다.
 * Mirror of locarora-admin/lib/supabase/timeout-fetch.ts.
 */
function isRetriableFetchError(err: unknown): boolean {
  if (err instanceof DOMException) {
    return err.name === "TimeoutError" || err.name === "AbortError";
  }
  if (err instanceof Error) {
    if (err.name === "TimeoutError" || err.name === "AbortError") return true;
    return /fetch failed|ECONNRESET|ETIMEDOUT|socket hang up|network|EAI_AGAIN/i.test(
      err.message ?? "",
    );
  }
  return false;
}

/** 재시도 전 대기. 포화된 pooler 가 슬롯을 비울 최소한의 틈. */
const RETRY_BACKOFF_MS = 300;

/**
 * `totalBudget` 이 있는 티어에서, 남은 예산이 이보다 적으면 재시도하지 않는다.
 * 어차피 못 끝낼 요청을 한 번 더 걸어 실패를 늦추기만 하는 것을 막는다.
 */
const MIN_RETRY_TIMEOUT_MS = 1_000;

export function makeTimeoutFetch(
  timeouts: TieredTimeouts,
  opts: { retryReads?: boolean; retryReadOnlyRpc?: boolean } = {},
): typeof fetch {
  // Middleware runs on every request and is intentionally fail-fast (treat
  // anonymous on timeout), so it opts out — a retry would defeat the 4s cap.
  const retryReads = opts.retryReads ?? true;
  // `/rpc/*` 는 POST 라 기본적으로 절대 재생하지 않는다(부수효과 있는 RPC 를
  // 두 번 실행할 수 없다). 호출하는 RPC 가 전부 읽기 전용이라고 **클라이언트
  // 단위로** 보장할 수 있을 때만 켠다 — 현재는 쿠키리스 캐시 클라이언트뿐이다
  // (get_home_page_product_ids / search_products_by_bounds /
  //  get_product_branch_summaries 등 `get_*`·`search_*` 조회 전용).
  const retryReadOnlyRpc = opts.retryReadOnlyRpc ?? false;
  return async (input, init) => {
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : input.url;
    const method =
      init?.method ?? (typeof input === "object" && "method" in input ? input.method : undefined);

    const tier = pickTier(url, method);
    const timeoutMs = timeouts[tier];
    const accept = acceptHeaderOf(input, init);

    const report = (
      outcome: SupabaseFetchTiming["outcome"],
      elapsedMs: number,
      attemptNo: number,
    ) => {
      if (!timingReporter) return;
      try {
        timingReporter({
          endpoint: pathOf(url),
          method: method ?? "GET",
          tier,
          timeoutMs,
          elapsedMs: Math.round(elapsedMs),
          attempt: attemptNo,
          outcome,
        });
      } catch {
        // 계측이 요청을 깨뜨리면 안 된다.
      }
    };

    // Only idempotent reads are auto-retried — a mutation (POST/PATCH/DELETE,
    // /rpc/*) must never be replayed. GET /auth/v1/* covers the JWKS fetch
    // behind getClaims(), whose unretried stalls surfaced as WEB-4Q.
    const isWrite = method && method !== "GET" && method !== "HEAD";
    const isRpc = url.includes("/rpc/");
    const isIdempotentRead =
      (!isWrite && (url.includes("/rest/v1/") || url.includes("/auth/v1/"))) ||
      (retryReadOnlyRpc && isRpc);

    const attempt = async (
      attemptNo: number,
      attemptTimeoutMs: number,
    ): Promise<Response> => {
      const timeoutSignal = AbortSignal.timeout(attemptTimeoutMs);
      let signal: AbortSignal = timeoutSignal;
      if (init?.signal) {
        try {
          signal = AbortSignal.any([timeoutSignal, init.signal]);
        } catch {
          signal = init.signal;
        }
      }
      // Date.now()가 아니라 monotonic clock — 벽시계 보정에 흔들리지 않는다.
      const startedAt = performance.now();
      let res: Response;
      try {
        res = await fetch(input, { ...init, signal });
        const elapsed = performance.now() - startedAt;
        if (elapsed >= attemptTimeoutMs * SLOW_SUCCESS_RATIO) {
          report("slow", elapsed, attemptNo);
        }
      } catch (err) {
        report("timeout", performance.now() - startedAt, attemptNo);
        throw err;
      }
      // 416/PGRST103 → 빈 페이지 변환은 1차 시도와 재시도 **양쪽**에 걸려야 하므로
      // 여기서 한다. 타이밍 계측(try) 밖이라 변환이 timeout 으로 오보되지 않는다.
      return emptyPageOnOutOfRange(res, url, method, accept);
    };

    const callStartedAt = performance.now();
    try {
      return await attempt(1, timeoutMs);
    } catch (err) {
      // Don't retry if the caller's own signal aborted (intentional cancel) or
      // it isn't a transient connection blip on an idempotent read.
      if (
        retryReads &&
        isIdempotentRead &&
        !(init?.signal?.aborted ?? false) &&
        isRetriableFetchError(err)
      ) {
        // `totalBudget` 이 있으면 재시도는 **남은 예산**만 쓴다 — 티어 예산을
        // 키워도 (1차 + 백오프 + 재시도) 가 상한을 넘지 않는다.
        let retryTimeoutMs = timeoutMs;
        if (timeouts.totalBudget !== undefined) {
          const elapsed = performance.now() - callStartedAt;
          // performance.now() 는 소수 ms 를 돌려주므로 잔여 예산도 소수가 된다.
          // Node 22 의 AbortSignal.timeout 은 정수만 받아 RangeError 를 던지고,
          // 그 throw 가 재시도 자체를 없애 버렸다(LOCARORA-WEB-6H). 내림으로 맞춘다.
          const remaining = Math.floor(
            timeouts.totalBudget - elapsed - RETRY_BACKOFF_MS,
          );
          retryTimeoutMs = Math.min(timeoutMs, remaining);
          if (retryTimeoutMs < MIN_RETRY_TIMEOUT_MS) throw err;
        }
        await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS));
        return attempt(2, retryTimeoutMs);
      }
      throw err;
    }
  };
}
